import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { eventInfo } from '../data';
import { MapPin, Mail, ExternalLink, ShieldCheck, Ticket, Building2, ScanLine, LayoutGrid, UserCheck, Store } from 'lucide-react';
import { fetchApiVersion, BackendVersionInfo } from '../lib/api';

export function Footer() {
  const webCommit = import.meta.env.VITE_WEB_COMMIT_SHA || 'unknown';
  const [backendVersion, setBackendVersion] = useState<BackendVersionInfo | null>(null);

  useEffect(() => {
    let isMounted = true;
    fetchApiVersion().then((info) => {
      if (isMounted) {
        setBackendVersion(info);
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);
  return (
    <footer className="mt-20 border-t border-slate-200 bg-white text-xs text-slate-600">
      {/* Upper Footer Links */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {/* Brand & Legal Entity Disclaimer */}
        <div className="space-y-3">
          <div className="flex items-center gap-2.5">
            <img
              src="/logo/iiab_logo.png"
              alt="IIAB 2027 Logo"
              className="h-8 sm:h-9 w-auto object-contain"
            />
            <div>
              <span className="font-black text-slate-900 text-sm tracking-tight">IIAB 2027</span>
              <span className="block text-[10px] text-[#0A3D27] font-bold">{eventInfo.edition}</span>
            </div>
          </div>
          <p className="text-slate-600 text-xs leading-relaxed">
            Organized by <strong className="text-slate-900">{eventInfo.organizer.legalEntity}</strong> ({eventInfo.organizer.initiative}).
            India's most focused B2B agrochemicals and specialty agricultural inputs conference.
          </p>
          <div className="pt-1 text-[11px] text-slate-500 space-y-1">
            <p className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-[#0A3D27] shrink-0" />
              <span>{eventInfo.venue.name}, Mumbai</span>
            </p>
            <p className="font-mono text-slate-500">
              GSTIN: <span className="text-slate-800 font-semibold">{eventInfo.organizer.gstin}</span>
            </p>
          </div>
        </div>

        {/* 1. Public Details & Event Info */}
        <div className="space-y-3">
          <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Public Details</h4>
          <ul className="space-y-2">
            <li>
              <Link to="/#hero" className="text-slate-600 hover:text-[#0A3D27] transition-colors">
                Home Overview & Hero
              </Link>
            </li>
            <li>
              <Link to="/#impact" className="text-slate-600 hover:text-[#0A3D27] transition-colors">
                Strategic Market Growth (Impact)
              </Link>
            </li>
            <li>
              <Link to="/#sectors" className="text-slate-600 hover:text-[#0A3D27] transition-colors">
                Focus Industry Sectors
              </Link>
            </li>
            <li>
              <Link to="/#keynotes" className="text-slate-600 hover:text-[#0A3D27] transition-colors">
                Keynote Speakers & Agenda
              </Link>
            </li>
            <li>
              <Link to="/#sponsors" className="text-slate-600 hover:text-[#0A3D27] transition-colors">
                Sponsors & Global Partners
              </Link>
            </li>
            <li>
              <Link to="/#logistics" className="text-slate-600 hover:text-[#0A3D27] transition-colors">
                Venue & Logistics (JWCC Pavilion 3)
              </Link>
            </li>
            <li>
              <Link to="/terms" className="text-slate-600 hover:text-[#0A3D27] transition-colors">
                Terms & Conditions
              </Link>
            </li>
            <li>
              <Link to="/terms?tab=privacy" className="text-slate-600 hover:text-[#0A3D27] transition-colors">
                Privacy Policy & Turnstile Telemetry
              </Link>
            </li>
            <li>
              <a
                href={eventInfo.brochureUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-600 hover:text-[#0A3D27] transition-colors flex items-center gap-1"
              >
                <span>Event Brochure (PDF)</span>
                <ExternalLink className="w-3 h-3 text-slate-400" />
              </a>
            </li>
          </ul>
        </div>

        {/* 2 & 3. Visitors & Stall Booking */}
        <div className="space-y-3">
          <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Visitors & Stalls</h4>
          <ul className="space-y-2.5">
            <li className="space-y-1">
              <div className="text-[10px] font-bold text-cyan-800 uppercase tracking-wider">Visitor Services</div>
              <div className="space-y-1 pl-1">
                <Link to="/register" className="text-slate-600 hover:text-cyan-800 transition-colors flex items-center gap-1.5">
                  <Ticket className="w-3.5 h-3.5 text-cyan-700" />
                  <span>Register to Attend (Pricing Tiers)</span>
                </Link>
                <Link to="/my-iiab" className="text-slate-600 hover:text-cyan-800 transition-colors flex items-center gap-1.5">
                  <UserCheck className="w-3.5 h-3.5 text-cyan-700" />
                  <span>Existing Participant Flow (My IIAB)</span>
                </Link>
              </div>
            </li>

            <li className="space-y-1 pt-1 border-t border-slate-100">
              <div className="text-[10px] font-bold text-[#0A3D27] uppercase tracking-wider">Stall Services</div>
              <div className="space-y-1 pl-1">
                <Link to="/floorplan" className="text-slate-600 hover:text-[#0A3D27] transition-colors flex items-center gap-1.5">
                  <LayoutGrid className="w-3.5 h-3.5 text-[#0A3D27]" />
                  <span>New Booking Flow (Floor Plan)</span>
                </Link>
                <Link to="/exhibitor-portal" className="text-slate-600 hover:text-[#0A3D27] transition-colors flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5 text-[#0A3D27]" />
                  <span>Existing Booth Flow (Portal)</span>
                </Link>
              </div>
            </li>
          </ul>
        </div>

        {/* 4. Admin & Day-of-Event Operations */}
        <div className="space-y-3">
          <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Admin Console</h4>
          <p className="text-xs text-slate-600">
            Sales management, offline allocations, turnstile check-in & badge printing helpdesk.
          </p>

          <div className="space-y-1.5 text-xs">
            <a
              href={`mailto:${eventInfo.organizer.contactEmail}`}
              className="flex items-center gap-2 text-slate-600 hover:text-[#0A3D27] transition-colors"
            >
              <Mail className="w-3.5 h-3.5 text-[#0A3D27]" />
              <span>{eventInfo.organizer.contactEmail}</span>
            </a>
            <a
              href={`mailto:${eventInfo.organizer.supportEmail}`}
              className="flex items-center gap-2 text-slate-600 hover:text-[#0A3D27] transition-colors"
            >
              <Mail className="w-3.5 h-3.5 text-[#0A3D27]" />
              <span>{eventInfo.organizer.supportEmail}</span>
            </a>
          </div>

          <div className="pt-2 flex flex-wrap items-center gap-2">
            <Link
              to="/exhibitor-portal"
              className="px-2.5 py-1.5 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg text-[11px] font-bold text-purple-900 transition-colors flex items-center gap-1.5 shadow-xs"
            >
              <Store className="w-3 h-3 text-purple-700" />
              <span>Exhibitor Portal</span>
            </Link>
            <Link
              to="/my-iiab"
              className="px-2.5 py-1.5 bg-sky-50 hover:bg-sky-100 border border-sky-200 rounded-lg text-[11px] font-bold text-sky-900 transition-colors flex items-center gap-1.5 shadow-xs"
            >
              <Ticket className="w-3 h-3 text-sky-700" />
              <span>Visitor Portal</span>
            </Link>
            <Link
              to="/apply"
              className="px-2.5 py-1.5 bg-[#FAF5EA] hover:bg-[#F5E8C7] border border-[#E6C687] rounded-lg text-[11px] font-bold text-[#785613] transition-colors flex items-center gap-1.5 shadow-xs"
            >
              <UserCheck className="w-3 h-3 text-[#785613]" />
              <span>Apply: Speakers / Pods / Press</span>
            </Link>
          </div>

          <div className="pt-2 border-t border-slate-100">
            <Link
              to="/admin"
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 hover:text-[#0A3D27] transition-colors"
            >
              <ShieldCheck className="w-3.5 h-3.5 text-slate-400" />
              <span>Staff &amp; Operations Console</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom Legal Copyright Bar */}
      <div className="border-t border-slate-100 bg-[#F8F9F6] px-4 sm:px-6 py-6 text-center text-[11px] text-slate-500">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p>
            IIAB 2027 • PostgreSQL Row-Level Atomic Locking • Vite React 19 • © {new Date().getFullYear()}{' '}
            {eventInfo.organizer.legalEntity}
          </p>
          <div className="flex flex-wrap items-center justify-center sm:justify-end gap-2 text-slate-400">
            <span className="hidden md:inline">Executive Agro-Tech Platform • Enterprise Architecture</span>
            <span className="hidden md:inline text-slate-300">•</span>
            {/* Subtle deployment commit indicators */}
            <div className="inline-flex items-center gap-1.5 text-[10px]">
              {/* Cloudflare Pages Frontend Commit */}
              <a
                href={webCommit && webCommit !== 'unknown' ? `https://github.com/ray-consulting-panda/iiab-platform/commit/${webCommit}` : undefined}
                target="_blank"
                rel="noopener noreferrer"
                title={`Cloudflare Pages Frontend: commit ${webCommit}`}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-200/70 hover:bg-slate-300/80 text-slate-600 transition-colors font-mono cursor-pointer"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#F38020]" />
                <span>Pages: {webCommit}</span>
              </a>

              {/* Render Backend Commit & Live Status */}
              <a
                href={backendVersion?.commit && backendVersion.commit !== 'unknown' && backendVersion.commit !== 'unreachable' ? `https://github.com/ray-consulting-panda/iiab-platform/commit/${backendVersion.commit}` : undefined}
                target="_blank"
                rel="noopener noreferrer"
                title={backendVersion ? `Render API Backend: commit ${backendVersion.commit} (${backendVersion.status === 'ok' ? 'Online' : 'Offline'})` : 'Render API Backend: Checking status...'}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-200/70 hover:bg-slate-300/80 text-slate-600 transition-colors font-mono cursor-pointer"
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    backendVersion?.status === 'ok'
                      ? 'bg-emerald-500'
                      : !backendVersion
                      ? 'bg-amber-400 animate-pulse'
                      : 'bg-rose-400'
                  }`}
                />
                <span>
                  Render: {backendVersion ? backendVersion.commit : 'checking...'}
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
